import React from "react";
import PropTypes from "prop-types";
import { ListGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

import {
  PASSWORD_LENGTH_ERROR,
  passwordValidationMessages,
} from "../../../constants";
import { getConditionalValues } from "../../utility";

const ValidationRow = ({ isValid, message, ariaLabel }) => (
  <div
    className={`bold-4 ${getConditionalValues(
      isValid,
      "text-success",
      "text-danger",
    )}`}
    aria-label={ariaLabel}
  >
    <span>
      <FontAwesomeIcon
        icon={getConditionalValues(isValid, faCheck, faTimes)}
        className="pe-1"
        size={isValid ? "sm" : undefined}
      />
    </span>
    <small>{message}</small>
  </div>
);

ValidationRow.propTypes = {
  isValid: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

/**
 * Shared password-rules checklist (number, capital, special, length, optional match).
 * Used by account setup and reset-password screens.
 */
const PasswordValidationChecklist = ({
  checkValidation,
  showEquality = true,
  listGroupClassName = "my-2",
}) => {
  const {
    checkCapLetter,
    checkNumber,
    checkSpecialChar,
    checkLength,
    checkEquality,
  } = checkValidation;

  const isAllValid = Object.values(checkValidation).every(
    (val) => val === true,
  );

  return (
    <ListGroup className={listGroupClassName}>
      <ListGroup.Item variant={isAllValid ? "success" : "danger"}>
        <ValidationRow
          isValid={checkNumber}
          message={passwordValidationMessages.NUMBER}
          ariaLabel="number-validation-label"
        />
        <ValidationRow
          isValid={checkCapLetter}
          message={passwordValidationMessages.CAPITAL}
          ariaLabel="capital-letter-validation-label"
        />
        <ValidationRow
          isValid={checkSpecialChar}
          message={passwordValidationMessages.SPECIAL_CHAR}
          ariaLabel="special-char-validation-label"
        />
        <ValidationRow
          isValid={checkLength}
          message={PASSWORD_LENGTH_ERROR}
          ariaLabel="length-validation-label"
        />
        {showEquality && (
          <ValidationRow
            isValid={checkEquality}
            message={passwordValidationMessages.MATCH}
            ariaLabel="both-password-match-validation-label"
          />
        )}
      </ListGroup.Item>
    </ListGroup>
  );
};

PasswordValidationChecklist.propTypes = {
  checkValidation: PropTypes.shape({
    checkCapLetter: PropTypes.bool,
    checkNumber: PropTypes.bool,
    checkSpecialChar: PropTypes.bool,
    checkLength: PropTypes.bool,
    checkEquality: PropTypes.bool,
  }).isRequired,
  showEquality: PropTypes.bool,
  listGroupClassName: PropTypes.string,
};

export default PasswordValidationChecklist;
